import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Trans } from 'react-i18next'
import { ErrorBoundary } from '../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { BackButton } from '../../../components/common/BackButton'
import { IllustrationChoices } from '../../../components/common/Illustration'
import { CreateSocietyTypeCard } from './CreateSocietyTypeCard'
import { SocietyType } from './SocietyType'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { reverseUrl } from '../../../navigation/reverseUrl'
import {
  BOAPPA_DEMO_LINK,
  KUNDO_BOARD_GUIDE_LINK,
  KUNDO_SUPPORT_GUIDE_LINK,
} from '../../../constants/Links'
import { i18nSocietyType } from '../../../i18n'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'

export const CreateSocietyTypeSelectionView = (): JSX.Element => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const navigate = useNavigate()
  useDocumentTitle(reverseDocumentTitle('create-society:pick-type'))

  const goBack = (): void => {
    navigate(-1)
  }

  return (
    <ErrorBoundary>
      <div className="h-full w-full space-y-10 overflow-y-auto p-6">
        <div className="flex items-center space-x-4">
          <BackButton onClick={goBack} />
          <p style={theme.textVariants.h2}>
            {translate('createSocietyTypeSelectionView.title', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
          </p>
        </div>
        <div className="container">
          <h2 style={theme.textVariants.h1}>
            {translate('createSocietyTypeSelectionView.header', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}{' '}
            <span className="text-brandBlue">Boappa</span>
          </h2>

          <p style={theme.textVariants.paragraph} className="mt-4">
            {translate('createSocietyTypeSelectionView.paragraph', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
          </p>
          <p style={theme.textVariants.paragraph} className="mt-4">
            <Trans
              i18nKey="createSocietyTypeSelectionView.paragraph2"
              components={{
                GuideLink: (
                  <Link
                    to={KUNDO_BOARD_GUIDE_LINK}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brandBlue underline"
                  />
                ),
                DemoLink: (
                  <Link
                    to={BOAPPA_DEMO_LINK}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brandBlue underline"
                  />
                ),
                SupportLink: (
                  <Link
                    to={KUNDO_SUPPORT_GUIDE_LINK}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brandBlue underline"
                  />
                ),
              }}
            />
          </p>
          <p style={theme.textVariants.paragraph} className="mt-4">
            {translate('createSocietyTypeSelectionView.paragraph3', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
          </p>
        </div>
        <div className="flex w-full flex-col space-y-4">
          <div className="flex w-full flex-row flex-wrap gap-6">
            <CreateSocietyTypeCard
              className="w-full md:w-80 text-start"
              type={SocietyType.HOUSING_COOPERATIVE}
              title={translate(
                'createSocietyTypeSelectionView.labels.housingCooperative'
              )}
              illustration={IllustrationChoices.BUILDINGS}
            />
            <CreateSocietyTypeCard
              className="w-full md:w-80"
              type={SocietyType.HOMEOWNERS_ASSOCIATION}
              title={translate(
                'createSocietyTypeSelectionView.labels.homeownersAssociation'
              )}
              illustration={IllustrationChoices.WATERING_PLANT}
            />
          </div>
        </div>
        <p style={theme.textVariants.paragraph} className="mt-4">
          <Trans
            i18nKey="createSocietyTypeSelectionView.findSociety"
            components={{
              Link: (
                <Link
                  to={reverseUrl('find-society')}
                  className="text-brandBlue underline"
                />
              ),
            }}
          />
        </p>
      </div>
    </ErrorBoundary>
  )
}
